import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';

export default function AdminApplicationPage() {
  const [applications, setApplications] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await api.get('/applications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(res.data);
      setFiltered(res.data);
    } catch (err) {
      toast.error('Failed to load applications');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/admin';
  };

  const handleSearch = e => {
    const keyword = e.target.value.toLowerCase();
    setSearch(keyword);
    setFiltered(
      applications.filter(
        app =>
          app.name.toLowerCase().includes(keyword) ||
          app.email.toLowerCase().includes(keyword) ||
          app.internship_title.toLowerCase().includes(keyword)
      )
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await api.delete(`/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application deleted');
      fetchApplications();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const exportExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(filtered);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Applications');
    const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'applications.xlsx');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const tableData = filtered.map((app, i) => [
      i + 1,
      app.internship_title,
      app.name,
      app.email,
      app.phone,
      app.qualification,
      app.reason,
      new Date(app.submitted_at).toLocaleString()
    ]);

    autoTable(doc, {
      head: [['#', 'Internship', 'Name', 'Email', 'Phone', 'Qualification', 'Reason', 'Submitted At']],
      body: tableData
    });

    doc.save('applications.pdf');
  };

  return (
    <div className="container py-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Internship Applications</h3>
        <div className="d-flex gap-2">
          <button onClick={() => navigate('/admin/dashboard')} className="btn btn-outline-primary btn-sm">
            ← Dashboard
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between mb-3">
        <input
          type="text"
          className="form-control me-2"
          placeholder="Search by name, email or internship"
          value={search}
          onChange={handleSearch}
        />
        <div>
          <button className="btn btn-sm btn-outline-success me-2" onClick={exportExcel}>
            Export Excel
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={exportPDF}>
            Export PDF
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p>No matching applications found.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Internship</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Qualification</th>
                <th>Reason</th>
                <th>Submitted At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((app, index) => (
                <tr key={app.id}>
                  <td>{index + 1}</td>
                  <td>{app.internship_title}</td>
                  <td>{app.name}</td>
                  <td>{app.email}</td>
                  <td>{app.phone}</td>
                  <td>{app.qualification}</td>
                  <td>{app.reason}</td>
                  <td>{new Date(app.submitted_at).toLocaleString()}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(app.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
